import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Clock, Sparkles, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { findTreatment } from "@/data/treatments";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_unified-calendar-app-2/artifacts/7wohqirg_Texto%20del%20pa%CC%81rrafo.jpeg";

export default function Treatment() {
  const { slug } = useParams();
  const t = findTreatment(slug);
  if (!t) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F4EFE4]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-[#F4EFE4]/90 border-b border-[#E8DFD2]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="treatment-logo">
            <img src={LOGO_URL} alt="Amarme" className="h-10 w-10 rounded-full object-cover border border-[#B5704A]/20" />
            <div className="leading-none">
              <div className="brand-wordmark text-base text-[#3A2418]">AMARME</div>
              <div className="font-script text-xs text-[#B5704A] -mt-0.5">Skin studio</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#6F5A4C] hover:text-[#B5704A] transition-colors" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Volver al sitio
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-16">
        <Link to="/#servicios" className="text-xs label-eyebrow text-[#B5704A] hover:underline">
          ← Tratamientos
        </Link>
        <div className="grid lg:grid-cols-2 gap-12 mt-4 items-start">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-display text-5xl sm:text-6xl font-[300] text-[#3A2418] leading-[1.05]" data-testid="treatment-title">
              {t.title}
            </h1>
            <p className="text-lg text-[#6F5A4C] mt-4 italic font-display">
              {t.tagline}
            </p>
            <div className="flex items-center gap-3 mt-6 text-sm text-[#3A2418]">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#E8DFD2]">
                <Clock className="w-4 h-4 text-[#B5704A]" />
                <span className="font-medium">{t.duration}</span>
              </div>
            </div>
            <p className="text-[#3A2418] mt-8 leading-relaxed">{t.description}</p>

            <Link to={`/reservar?servicio=${t.slug}`} className="inline-block mt-8" data-testid="treatment-book-cta">
              <Button size="lg" className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full px-8 h-12">
                Agendar este tratamiento <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[#E8DFD2]/60 rounded-[3rem] rotate-2"></div>
            <img src={t.image} alt={t.title} className="relative rounded-[2rem] border border-[#E8DFD2] w-full aspect-[4/5] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Detail sections */}
      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-6">
        {t.idealFor && (
          <div className="surface-card p-8">
            <span className="label-eyebrow text-[#B5704A]">Ideal para</span>
            <ul className="mt-4 space-y-3">
              {t.idealFor.map((i) => (
                <li key={i} className="flex items-start gap-2 text-[#3A2418]">
                  <Sparkles className="w-4 h-4 text-[#B5704A] mt-1 shrink-0" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {t.helpsWith && (
          <div className="surface-card p-8">
            <span className="label-eyebrow text-[#B5704A]">Ayuda a mejorar</span>
            <ul className="mt-4 space-y-3">
              {t.helpsWith.map((i) => (
                <li key={i} className="flex items-start gap-2 text-[#3A2418]">
                  <Sparkles className="w-4 h-4 text-[#B5704A] mt-1 shrink-0" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {t.benefits && t.benefits.length > 0 && (
          <div className="surface-card p-8">
            <span className="label-eyebrow text-[#B5704A]">✨ Beneficios</span>
            <ul className="mt-4 space-y-3">
              {t.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-[#3A2418]">
                  <CheckCircle2 className="w-4 h-4 text-[#B5704A] mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {t.result && (
          <div className="surface-card p-8 bg-[#3A2418] text-[#FAE0CE] border-[#3A2418]">
            <span className="label-eyebrow text-[#E8B79A]">✨ Resultado</span>
            <p className="font-display text-2xl italic font-[400] mt-4 leading-snug">{t.result}</p>
          </div>
        )}
      </section>

      {t.note && (
        <section className="max-w-6xl mx-auto px-6 pb-12">
          <div className="surface-card p-5 flex items-start gap-3 bg-[#FDF3EF] border-l-4 border-l-[#D87D65]">
            <AlertCircle className="w-5 h-5 text-[#D87D65] mt-0.5 shrink-0" />
            <p className="text-sm text-[#3A2418]">{t.note}</p>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-[2rem] bg-gradient-to-br from-[#B5704A] to-[#7C4A2F] text-white p-12 text-center grain">
          <h2 className="font-display text-3xl sm:text-4xl font-[300]">
            ¿Lista para tu <span className="italic font-[500]">{t.title}?</span>
          </h2>
          <Link to={`/reservar?servicio=${t.slug}`} className="inline-block mt-6">
            <Button size="lg" className="bg-white text-[#3A2418] hover:bg-[#FAE0CE] hover:text-[#3A2418] rounded-full px-8 h-12">
              Agendar ahora
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
